import React, { useState, useEffect } from 'react';
import { useAppContext } from '../../context/AppContext';
import { api } from '../../lib/api';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiSave, FiInfo, FiTag, FiHash } = FiIcons;

const PartModal = ({ part, prefill, onClose, onSuccess }) => {
  const { brandOptions, typeOptions } = useAppContext();

  // REFACTOR: Focus on part_numbr
  const [formData, setFormData] = useState({
    part_numbr: '',
    descrip1: '',
    descrip2: '',
    appl_brand: '',
    equip_type: '',
    part_type: '',
    oem_pn: '',
    status: 'Active',
    comments: ''
  });

  useEffect(() => {
    if (!part && prefill) setFormData((f) => ({ ...f, ...prefill }));
    if (part) {
      setFormData({
        part_numbr: part.part_numbr || '',
        descrip1: part.descrip1 || '',
        descrip2: part.descrip2 || '',
        appl_brand: part.appl_brand || '',
        equip_type: part.equip_type || '',
        part_type: part.part_type || '',
        oem_pn: part.oem_pn || '',
        status: part.status || 'Active',
        comments: part.comments || ''
      });
    }
  }, [part, prefill]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api('adminSave', { table: 'parts', id: part?.id, data: formData });
      onSuccess(part ? 'Part updated successfully' : 'New part created');
      onClose();
    } catch (err) {
      alert(`Database Error: ${err.message}`);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/50 dark:bg-slate-950/80 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        
        <div className="p-6 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-900/50">
          <div className="text-left">
            <h2 className="text-xl font-black text-slate-900 dark:text-white flex items-center gap-2">
              <SafeIcon icon={part ? FiInfo : FiSave} className="text-blue-600 dark:text-blue-500" />
              {part ? `Edit Part: ${part.part_numbr}` : 'Manual Inventory Entry'}
            </h2>
            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-1">
              Updating master inventory database
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-full text-slate-400 transition-colors">
            <SafeIcon icon={FiX} className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-8 text-left">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <SectionTitle icon={FiHash} title="Primary Identification" />
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Part Number (Required) *</label>
              <input 
                name="part_numbr" 
                required 
                className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl p-3 text-sm text-slate-900 dark:text-white outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition-all font-mono" 
                value={formData.part_numbr} 
                onChange={handleChange}
                placeholder="e.g. W10408179"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider">OEM PN</label>
              <input 
                name="oem_pn" 
                className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl p-3 text-sm text-slate-900 dark:text-white outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition-all font-mono" 
                value={formData.oem_pn} 
                onChange={handleChange} 
              />
            </div>

            <div className="md:col-span-2 space-y-1.5">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Primary Description *</label>
              <input 
                name="descrip1" 
                required 
                className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl p-3 text-sm text-slate-900 dark:text-white outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition-all" 
                value={formData.descrip1} 
                onChange={handleChange}
                placeholder="Brief description of the part"
              />
            </div>

            <div className="md:col-span-2 mt-4">
              <SectionTitle icon={FiTag} title="Classification & Sorting" />
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Brand</label>
              <select 
                name="appl_brand" 
                className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl p-3 text-sm text-slate-900 dark:text-white outline-none focus:border-blue-500 transition-all"
                value={formData.appl_brand}
                onChange={handleChange}
              >
                {brandOptions.map(opt => (
                  <option key={opt.value} value={opt.value === 'All' ? '' : opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Equipment Type</label>
              <select 
                name="equip_type" 
                className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl p-3 text-sm text-slate-900 dark:text-white outline-none focus:border-blue-500 transition-all"
                value={formData.equip_type}
                onChange={handleChange}
              >
                {typeOptions.map(opt => (
                  <option key={opt.value} value={opt.value === 'All' ? '' : opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Status</label>
              <select 
                name="status" 
                className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl p-3 text-sm text-slate-900 dark:text-white outline-none focus:border-blue-500 transition-all"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="Active">Active</option>
                <option value="Discontinued">Discontinued (NLA)</option>
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Part Category</label>
              <input 
                name="part_type" 
                className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl p-3 text-sm text-slate-900 dark:text-white outline-none focus:border-blue-500 transition-all" 
                value={formData.part_type} 
                onChange={handleChange}
                placeholder="e.g. Valve, Motor"
              />
            </div>

            <div className="md:col-span-2 space-y-1.5 mt-2">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Technician Comments</label>
              <textarea 
                name="comments" 
                className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl p-4 text-sm text-slate-900 dark:text-white outline-none focus:border-blue-500 h-24 resize-none transition-all" 
                value={formData.comments} 
                onChange={handleChange}
                placeholder="Internal notes for technicians..."
              />
            </div>
          </div>
        </form>

        <div className="p-6 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 flex justify-end gap-4">
          <button 
            type="button" 
            onClick={onClose}
            className="px-6 py-2.5 text-slate-600 dark:text-slate-400 font-bold hover:text-slate-900 dark:hover:text-white transition-colors"
          >
            Discard Changes
          </button>
          <button 
            onClick={handleSubmit}
            className="bg-blue-600 hover:bg-blue-500 text-white px-10 py-3 rounded-xl font-black text-sm flex items-center gap-2 shadow-lg shadow-blue-900/20 transition-all active:scale-95"
          >
            <SafeIcon icon={FiSave} />
            {part ? 'Update Database' : 'Publish to Inventory'}
          </button>
        </div>
      </div>
    </div>
  );
};

const SectionTitle = ({ icon, title }) => (
  <div className="flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-2 mb-2">
    <SafeIcon icon={icon} className="text-blue-500 w-3 h-3" />
    <span className="text-[10px] font-black text-slate-400 dark:text-slate-600 uppercase tracking-widest">{title}</span>
  </div>
);

export default PartModal;